import { useEffect } from 'react';
import { muteAll, useFetchOld } from 'hooks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getOrder } from '../actions';
import { ORDER_UPDATE_INTERVAL } from '../constants';
import { selectActiveTradingAccountId } from '../reducers/selectors';
export const lastAPICallTimesForOrder = {};
const shouldContinueUpdates = (order) => order.status === 'pending' || order.status === 'queued';
const useOrderCache = (id, tradingAccountId) => {
    const cache = useAppSelector((state) => state.invest.ordersCache[id]);
    const [, , , fetch] = useFetchOld(muteAll);
    useEffect(() => {
        let nextUpdateTimeout;
        let stillMounted = true;
        const update = async () => {
            if (!tradingAccountId)
                return;
            if (nextUpdateTimeout)
                clearTimeout(nextUpdateTimeout);
            const result = await fetch(getOrder(id, tradingAccountId)); // wait for request to return before scheduling next update
            if (!result || shouldContinueUpdates(result.order)) {
                if (nextUpdateTimeout)
                    clearTimeout(nextUpdateTimeout);
                // if component has already been unmounted, `stillMounted` will be `false`
                // and we still have it in this closure
                if (stillMounted) {
                    nextUpdateTimeout = setTimeout(update, ORDER_UPDATE_INTERVAL);
                }
            }
        };
        update();
        return () => {
            stillMounted = false;
            if (nextUpdateTimeout)
                clearTimeout(nextUpdateTimeout);
        };
    }, [id, tradingAccountId]);
    return cache;
};
export const useFilledOrderCache = (item) => {
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const cache = useAppSelector((state) => {
        const order = state.invest.ordersCache[item.id];
        if (order) {
            const { filled, filledTimestamp, commission, symbol } = order;
            if (filled && filledTimestamp) {
                return {
                    ...order,
                    filled,
                    symbol,
                    filledTimestamp,
                    commission: commission ?? { value: 0, currency: 'GBP' },
                };
            }
        }
        return undefined;
    });
    const dispatch = useAppDispatch();
    // confirmation url has a TTL (can expire), so we need to refetch the whole order everytime
    useEffect(() => {
        if (!lastAPICallTimesForOrder[item.id] || new Date().getTime() - lastAPICallTimesForOrder[item.id] > 2000) {
            if (tradingAccountId) {
                lastAPICallTimesForOrder[item.id] = new Date().getTime();
                dispatch(getOrder(item.id, tradingAccountId));
            }
        }
    }, [item, tradingAccountId]);
    return cache;
};
export default useOrderCache;
